import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { AmendmentFieldMarkersService } from '../../../../amendments/amendment-field-markers.service';
import {
  AMENDMENT_WIDGET_HOST,
  buildAmendmentRowFieldKey,
} from '../../../../amendments/amendment-field.model';
import { AmendmentIndicatorComponent } from '../../../../helpers/components/amendment-indicator/amendment-indicator.component';

/**
 * Shows the amendment indicator next to a form field that was amended in the displayed version.
 *
 * Place it inside a widget's template and name the field with `fieldKey` (or `fieldKeys` for a
 * label that covers several fields), plus `rowIndex` for a field in a repeatable row. With no
 * inputs it stands for the whole widget. The widget is found through the hosting form element.
 * Nothing renders outside a form presenter (designer, print), where no markers exist, or in a
 * presenter whose host has not turned the indicators on.
 */
@Component({
  selector: 'fb-amendment-field-indicator',
  imports: [AmendmentIndicatorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.visible]': 'visible()' },
  template: `
    @if (visible()) {
      <span class="amendment-field-badge" role="img" aria-label="Amended">
        <fb-amendment-indicator />
      </span>
    }
  `,
  styles: `
    :host {
      display: none;
    }

    :host.visible {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
    }

    .amendment-field-badge {
      display: inline-flex;
      align-items: center;
    }

    @media print {
      :host.visible {
        display: none !important;
      }
    }
  `,
})
export class AmendmentFieldIndicatorComponent {
  private readonly markers = inject(AmendmentFieldMarkersService, { optional: true });
  private readonly host = inject(AMENDMENT_WIDGET_HOST, { optional: true });

  /**
   * Key of the field inside its widget.
   */
  public readonly fieldKey = input<string | null | undefined>(undefined);

  /**
   * Keys of several fields that share one label; any of them being amended shows the indicator.
   */
  public readonly fieldKeys = input<readonly string[] | null | undefined>(undefined);

  /**
   * Index of the repeatable row the field belongs to.
   */
  public readonly rowIndex = input<number | null | undefined>(undefined);

  public readonly visible = computed(() => {
    if (!this.markers || !this.host?.payload) {
      return false;
    }

    return this.markers.isMarked(this.host.payload, this.resolveFieldKeys());
  });

  private resolveFieldKeys(): string[] {
    const keys = this.fieldKeys()?.length ? [...(this.fieldKeys() ?? [])] : [];
    const fieldKey = this.fieldKey();
    const rowIndex = this.rowIndex();

    if (fieldKey) {
      keys.push(fieldKey);
    }

    if (rowIndex === null || rowIndex === undefined) {
      return keys;
    }

    return keys.length
      ? keys.map((key) => buildAmendmentRowFieldKey(rowIndex, key))
      : [buildAmendmentRowFieldKey(rowIndex)];
  }
}
